import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Edit, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { hasPermission } from "../../access/permissions";
import {
  filterProducts,
  fetchProducts,
  fetchCategoryTypes,
  fetchColors,
} from "../../api/CatalogAPIs";
import {
  CatalogShell,
  EmptyState,
  resolveMediaUrl,
  displayAr,
  displayEn,
  stockTone,
} from "./catalogUi";

const PAGE_SIZE = 12;

const ProductsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canManage = hasPermission(authState.role, authState.permissions, "catalog.manage");
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search") || "";
  const categoryTypeId = searchParams.get("categoryTypeId") || "";
  const colorId = searchParams.get("colorId") || "";
  const personalization = searchParams.get("personalization") || "";
  const deposit = searchParams.get("deposit") || "";
  const active = searchParams.get("active") || "";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchParams((prev) => {
        const current = prev.get("search") || "";
        if (searchInput === current) return prev;
        const next = new URLSearchParams(prev);
        if (!searchInput) next.delete("search");
        else next.set("search", searchInput);
        next.delete("page");
        return next;
      });
    }, 400);
    return () => clearTimeout(handle);
  }, [searchInput, setSearchParams]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const { data: categoryTypes = [] } = useQuery({
    queryKey: ["catalogCategoryTypes"],
    queryFn: fetchCategoryTypes,
    enabled: canManage,
  });

  const { data: colors = [] } = useQuery({
    queryKey: ["catalogColors"],
    queryFn: fetchColors,
    enabled: canManage,
  });

  const extraFilters = Boolean(personalization || deposit || active === "inactive");
  const filterQuery = useMemo(
    () => ({
      search: search || undefined,
      categoryTypeId: categoryTypeId || undefined,
      colorId: colorId || undefined,
      page: extraFilters ? 1 : page,
      pageSize: extraFilters ? 100 : PAGE_SIZE,
      activeOnly: active === "active",
    }),
    [search, categoryTypeId, colorId, page, extraFilters, active]
  );

  const { data, isLoading: loadingFilter, error: filterError } = useQuery({
    queryKey: ["catalogProductsFilter", filterQuery],
    queryFn: () => filterProducts(filterQuery),
    enabled: canManage && !extraFilters,
  });

  const { data: allProducts = [], isLoading: loadingAll, error: allError } = useQuery({
    queryKey: ["catalogProductsAll", categoryTypeId],
    queryFn: () => fetchProducts({ categoryTypeId: categoryTypeId || undefined }),
    enabled: canManage && extraFilters,
  });

  const isLoading = extraFilters ? loadingAll : loadingFilter;
  const error = extraFilters ? allError : filterError;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  const items = extraFilters
    ? (allProducts || []).filter((item) => {
        const term = search.trim().toLowerCase();
        if (term) {
          const blob = `${item.name || ""} ${item.description || ""}`.toLowerCase();
          if (!blob.includes(term)) return false;
        }
        if (colorId && !(item.variants || []).some((variant) => String(variant.colorId) === String(colorId))) {
          return false;
        }
        if (personalization === "yes" && !item.allowsPersonalization) return false;
        if (personalization === "no" && item.allowsPersonalization) return false;
        if (deposit === "yes" && !item.requiresDeposit) return false;
        if (deposit === "no" && !item.requiresDeposit) return false;
        if (active === "inactive" && item.isActive) return false;
        if (active === "active" && !item.isActive) return false;
        return true;
      }).map((item) => ({
        ...item,
        inStock: item.requiresVariants
          ? (item.variants || []).some((variant) => variant.stockQuantity > 0 && variant.isAvailable)
          : item.stockQuantity > 0,
        variantCount: (item.variants || []).length,
        minPrice: item.basePrice,
        stockQuantity: item.requiresVariants
          ? (item.variants || []).reduce((sum, variant) => sum + (variant.stockQuantity || 0), 0)
          : item.stockQuantity,
      }))
    : data?.items || [];

  const filteredItems = useMemo(() => {
    if (extraFilters) return items;
    return items.filter((item) => {
      if (personalization === "yes" && !item.allowsPersonalization) return false;
      if (personalization === "no" && item.allowsPersonalization) return false;
      if (deposit === "yes" && !item.requiresDeposit) return false;
      if (deposit === "no" && !item.requiresDeposit) return false;
      if (active === "inactive" && item.inStock) return false;
      return true;
    });
  }, [items, personalization, deposit, active, extraFilters]);

  const totalCount = extraFilters ? filteredItems.length : data?.totalCount || 0;
  const pageItems = extraFilters
    ? filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : filteredItems;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (!canManage) return null;

  return (
    <CatalogShell
      title="المنتجات الجاهزة"
      subtitle="إدارة كتالوج المنتجات الجاهزة والمتغيرات"
      actions={
        <button
          onClick={() => navigate("/admin/catalog/products/new")}
          className="px-5 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold flex items-center gap-2 shadow"
        >
          <Plus size={18} /> إضافة منتج
        </button>
      }
    >
      <ToastContainer position="top-center" />

      <div className="bg-white/90 rounded-2xl shadow border border-white/30 p-4 mb-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
        <div className="relative xl:col-span-2">
          <Search className="absolute right-3 top-3 text-gray-400" size={18} />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="بحث عن منتج..."
            className="w-full pr-10 pl-3 py-2.5 rounded-xl border border-gray-200"
          />
        </div>
        <select
          value={categoryTypeId}
          onChange={(e) => setParam("categoryTypeId", e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
        >
          <option value="">كل أنواع الفئات</option>
          {categoryTypes.map((type) => (
            <option key={type.categoryTypeId} value={type.categoryTypeId}>
              {displayAr(type.name)}
            </option>
          ))}
        </select>
        <select
          value={colorId}
          onChange={(e) => setParam("colorId", e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
        >
          <option value="">كل الألوان</option>
          {colors.map((color) => (
            <option key={color.colorId} value={color.colorId}>
              {displayAr(color.name)}
            </option>
          ))}
        </select>
        <select
          value={personalization}
          onChange={(e) => setParam("personalization", e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
        >
          <option value="">التخصيص: الكل</option>
          <option value="yes">قابل للتخصيص</option>
          <option value="no">بدون تخصيص</option>
        </select>
        <select
          value={deposit}
          onChange={(e) => setParam("deposit", e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white"
        >
          <option value="">العربون: الكل</option>
          <option value="yes">يتطلب عربون</option>
          <option value="no">بدون عربون</option>
        </select>
        <select
          value={active}
          onChange={(e) => setParam("active", e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white xl:col-span-1"
        >
          <option value="">الحالة: الكل</option>
          <option value="active">نشط / متوفر</option>
          <option value="inactive">غير متوفر</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6">
          فشل تحميل المنتجات.
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse bg-white rounded-2xl h-72 border" />
          ))}
        </div>
      ) : pageItems.length === 0 ? (
        <div className="bg-white rounded-2xl shadow">
          <EmptyState text="لا توجد منتجات مطابقة للفلاتر" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {pageItems.map((product) => {
            const quantity =
              product.stockQuantity != null ? product.stockQuantity : product.inStock ? 10 : 0;
            const tone = stockTone(quantity);
            return (
              <div
                key={product.productId}
                className="bg-white/90 rounded-2xl shadow border border-white/30 overflow-hidden flex flex-col"
              >
                <div className="h-44 bg-gray-100">
                  {product.imageUrl ? (
                    <img
                      src={resolveMediaUrl(product.imageUrl)}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">لا توجد صورة</div>
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col gap-2">
                  <div>
                    <h3 className="font-bold text-lg text-gray-800">{displayAr(product.name)}</h3>
                    <p className="text-sm text-gray-500" dir="ltr">
                      {displayEn(product.name)}
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    {displayAr(product.categoryTypeName) || product.categoryName || "بدون فئة"}
                  </p>
                  <p className="font-bold text-indigo-700">
                    {product.minPrice || product.basePrice
                      ? `${product.minPrice || product.basePrice} EGP`
                      : "بدون سعر أساسي"}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.requiresDeposit && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Deposit Required
                      </span>
                    )}
                    {product.allowsPersonalization && (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        Personalized
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${tone.className}`}>
                      {product.isActive === false ? "معطّل" : tone.label}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700">
                      {product.variantCount || 0} متغير
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/catalog/products/${product.productId}`)}
                    className="mt-auto w-full py-2.5 rounded-xl bg-indigo-50 text-indigo-700 font-semibold flex items-center justify-center gap-2"
                  >
                    <Edit size={16} /> تعديل
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            disabled={page <= 1}
            onClick={() => setParam("page", String(page - 1))}
            className="p-2 rounded-xl bg-white border disabled:opacity-40"
          >
            <ChevronRight size={18} />
          </button>
          <span className="text-sm text-gray-600">
            صفحة {page} من {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setParam("page", String(page + 1))}
            className="p-2 rounded-xl bg-white border disabled:opacity-40"
          >
            <ChevronLeft size={18} />
          </button>
        </div>
      )}
    </CatalogShell>
  );
};

export default ProductsPage;
